import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Archive, RotateCcw } from "lucide-react";
import { adminTestEntries, adminArchiveTestEntries, adminRestoreArchived } from "@/lib/api";

const LABEL = {
  bookings: "Bookings",
  samples: "Sample requests",
  contacts: "Enquiries",
  guides: "Guide downloads",
  shares: "Visualiser shares",
};

export function TestSweepDialog({ adminKey, archivedCount = 0, onClose, onDone }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminTestEntries(adminKey)
      .then(setScan)
      .catch(() => {
        toast.error("Couldn't scan the inbox.");
        onClose();
      });
  }, [adminKey, onClose]);

  const archive = async () => {
    setBusy(true);
    try {
      const res = await adminArchiveTestEntries(adminKey);
      toast.success(`${res.archived.total} test entr${res.archived.total === 1 ? "y" : "ies"} archived`, {
        description: "Hidden from the inbox and the daily digest — nothing was deleted.",
      });
      onDone();
      onClose();
    } catch {
      toast.error("Couldn't archive the entries.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const res = await adminRestoreArchived(adminKey);
      toast.success(`${res.restored.total} entr${res.restored.total === 1 ? "y" : "ies"} restored`);
      onDone();
      onClose();
    } catch {
      toast.error("Couldn't restore.");
    } finally {
      setBusy(false);
    }
  };

  const total = scan?.counts?.total ?? 0;
  const kinds = scan ? Object.keys(LABEL).filter((k) => (scan.matches[k] || []).length) : [];

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-[#08090a]/80 px-4 py-10 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        data-testid="test-sweep-dialog"
        className="w-full max-w-2xl border border-[#8C7355]/25 bg-[#141518] p-6 md:p-9"
      >
        <div className="mb-7 flex items-start justify-between gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9A876]">Inbox hygiene</p>
            <h2 className="mt-2 font-serif-display text-3xl font-light italic tracking-tight text-[#F5F4F0]">
              Clear test entries
            </h2>
            <p className="mt-3 max-w-md font-body text-sm text-[#D6D1CA]/60">
              Anything below looks like an automated or placeholder submission. Archiving hides it
              from the inbox and the daily digest — the record stays in the database and can be
              restored.
            </p>
          </div>
          <button
            data-testid="test-sweep-close"
            onClick={onClose}
            aria-label="Close"
            className="p-2 text-[#D6D1CA]/60 hover:text-[#F5F4F0]"
            style={{ transition: "color 0.3s ease" }}
          >
            <X size={18} />
          </button>
        </div>

        {!scan && <p className="py-10 text-center font-body text-sm text-[#D6D1CA]/50">Scanning…</p>}

        {scan && total === 0 && (
          <div className="border border-dashed border-[#8C7355]/25 py-14 text-center">
            <p className="font-serif-display text-lg italic text-[#C9A876]/70">No test entries found</p>
            <p className="mt-3 font-body text-xs text-[#D6D1CA]/45">
              Every one of the {scan.counts.kept} record{scan.counts.kept === 1 ? "" : "s"} in your
              inbox looks like a genuine enquiry.
            </p>
          </div>
        )}

        {scan && total > 0 && (
          <div className="max-h-[45vh] space-y-6 overflow-y-auto pr-1" data-testid="test-sweep-list">
            {kinds.map((k) => (
              <section key={k}>
                <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#C9A876]/75">
                  {LABEL[k]} · {scan.matches[k].length}
                </p>
                <ul className="space-y-2">
                  {scan.matches[k].map((m) => (
                    <li
                      key={m.id}
                      data-testid={`test-sweep-row-${m.id}`}
                      className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border border-[#8C7355]/15 bg-[#1a1b1e]/60 px-4 py-3"
                    >
                      <span className="font-body text-sm text-[#F5F4F0]">
                        {m.name || "—"}{" "}
                        <span className="text-[#D6D1CA]/45">{m.email}</span>
                      </span>
                      <span className="font-body text-[11px] italic text-[#C9A876]/80">{m.reason}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#8C7355]/18 pt-6">
          {total > 0 && (
            <button
              data-testid="test-sweep-archive"
              onClick={archive}
              disabled={busy}
              className="btn-bronze inline-flex items-center gap-2 rounded-full px-7 py-3 text-[11px] font-medium uppercase tracking-[0.22em] disabled:opacity-60"
            >
              <Archive size={14} strokeWidth={1.6} />
              {busy ? "Archiving…" : `Archive ${total} entr${total === 1 ? "y" : "ies"}`}
            </button>
          )}
          {archivedCount > 0 && (
            <button
              data-testid="test-sweep-restore"
              onClick={restore}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border border-[#8C7355]/45 px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#F5F4F0] hover:bg-[#8C7355]/12 disabled:opacity-60"
              style={{ transition: "background-color 0.3s ease" }}
            >
              <RotateCcw size={13} strokeWidth={1.6} />
              Restore {archivedCount} archived
            </button>
          )}
          <button
            data-testid="test-sweep-cancel"
            onClick={onClose}
            className="ml-auto font-body text-[11px] uppercase tracking-[0.2em] text-[#D6D1CA]/60 hover:text-[#F5F4F0]"
            style={{ transition: "color 0.3s ease" }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}
